'use client';

import { allDataClear } from '@/model/dexie/ownerDexie';
import { getToken, saveTokenDataToDexie, type TokenData } from '@/model/dexie/userDexie';
import { getTabSync } from '@/utilities/tabSync';

// 토큰 갱신 상태 관리
type RefreshState = {
	isRefreshing: boolean;
	requestTime: number;
	tabId: string;
	promise: Promise<any> | null;
};

let refreshState: RefreshState = {
	isRefreshing: false,
	requestTime: 0,
	tabId: '',
	promise: null,
};

// 토큰 갱신 결과 타입
type RefreshResult = {
	success: boolean;
	accessToken?: string;
	refreshToken?: string;
	error?: string;
};

class TokenManager {
	private tabSync = getTabSync();
	private maxRetries = 3;
	private retryDelay = 1000; // 1초

	constructor() {
		this.setupTabSyncListeners();
	}

	private setupTabSyncListeners() {
		// 토큰 갱신 요청 수신
		this.tabSync.on('TOKEN_REFRESH_REQUEST', this.handleRefreshRequest.bind(this));

		// 토큰 갱신 결과 수신
		this.tabSync.on('TOKEN_REFRESH_RESULT', this.handleRefreshResult.bind(this));

		// 토큰 갱신 실패 수신
		this.tabSync.on('TOKEN_REFRESH_FAILED', this.handleRefreshFailed.bind(this));
	}

	private handleRefreshRequest = async (message: any) => {
		const { tabId, requestTime } = message.payload;

		// 이미 갱신 중이면 무시
		if (refreshState.isRefreshing && Date.now() - refreshState.requestTime < 30000) {
			return;
		}

		// 더 최근 요청이면 현재 탭이 처리
		if (requestTime > refreshState.requestTime) {
			refreshState.isRefreshing = true;
			refreshState.requestTime = requestTime;
			refreshState.tabId = this.tabSync.getTabId();

			try {
				const result = await this.performTokenRefresh();
				this.tabSync.broadcast('TOKEN_REFRESH_RESULT', result);
			} catch (error) {
				this.tabSync.broadcast('TOKEN_REFRESH_FAILED', {
					error: error instanceof Error ? error.message : String(error),
				});
			}
		}
	};

	private handleRefreshResult = async (message: any) => {
		const { success, accessToken, refreshToken } = message.payload;

		if (success && accessToken && refreshToken) {
			await saveTokenDataToDexie({ accessToken, refreshToken, index: 0 });
			console.log('[TokenManager] Token refreshed successfully via tab sync');
		}

		refreshState.isRefreshing = false;
		refreshState.promise = null;
	};

	private handleRefreshFailed = async (message: any) => {
		console.error('[TokenManager] Token refresh failed:', message.payload.error);
		refreshState.isRefreshing = false;
		refreshState.promise = null;

		// 모든 탭에서 로그아웃 처리
		await this.handleTokenExpired();
	};

	private async performTokenRefresh(): Promise<RefreshResult> {
		const token = await getToken();
		if (!token) {
			throw new Error('No token available for refresh');
		}

		const url = process.env.NEXT_PUBLIC_STEPIN_AUTH + '/auth/web/refresh';

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				accessToken: 'Bearer ' + token.accessToken,
				refreshToken: 'Bearer ' + token.refreshToken,
			},
		});

		if (!response.ok) {
			const errorData = await response.json().catch(() => ({}));
			throw new Error(errorData.message || 'Token refresh failed');
		}

		const accessToken = response.headers.get('Accesstoken');
		const refreshToken = response.headers.get('Refreshtoken');

		if (!accessToken || !refreshToken) {
			throw new Error('Invalid token response headers');
		}

		return {
			success: true,
			accessToken,
			refreshToken,
		};
	}

	// 메인 토큰 갱신 함수
	async refreshToken(): Promise<Response | null> {
		// 이미 갱신 중인 요청이 있으면 대기
		if (refreshState.promise) {
			try {
				return await refreshState.promise;
			} catch (error) {
				console.error('[TokenManager] Waiting for refresh failed:', error);
				return null;
			}
		}

		// 새로운 갱신 요청 생성
		refreshState.promise = this.initiateRefresh();

		try {
			return await refreshState.promise;
		} catch (error) {
			console.error('[TokenManager] Token refresh failed:', error);
			return null;
		} finally {
			refreshState.promise = null;
		}
	}

	private async initiateRefresh(): Promise<Response | null> {
		const requestTime = Date.now();

		// 다른 탭들에게 토큰 갱신 요청 알림
		this.tabSync.broadcast('TOKEN_REFRESH_REQUEST', {
			tabId: this.tabSync.getTabId(),
			requestTime,
		});

		// 현재 탭이 가장 먼저 요청했다면 직접 처리
		refreshState.isRefreshing = true;
		refreshState.requestTime = requestTime;
		refreshState.tabId = this.tabSync.getTabId();

		try {
			const result = await this.performTokenRefresh();

			if (result.success) {
				// 다른 탭들에게 결과 전파
				this.tabSync.broadcast('TOKEN_REFRESH_RESULT', result);

				// 현재 탭에서도 토큰 저장
				await saveTokenDataToDexie({
					accessToken: result.accessToken!,
					refreshToken: result.refreshToken!,
					index: 0,
				});

				// Response 객체 생성해서 반환 (기존 코드 호환성)
				const mockResponse = new Response(JSON.stringify({ success: true }), {
					status: 200,
					headers: {
						Accesstoken: result.accessToken!,
						Refreshtoken: result.refreshToken!,
					},
				});

				return mockResponse;
			} else {
				throw new Error('Token refresh failed');
			}
		} catch (error) {
			this.tabSync.broadcast('TOKEN_REFRESH_FAILED', { error: error instanceof Error ? error.message : String(error) });
			throw error;
		} finally {
			refreshState.isRefreshing = false;
		}
	}

	private async handleTokenExpired() {
		console.log('[TokenManager] Handling token expiration');

		// 점진적 로그아웃 처리
		try {
			await allDataClear();

			// 부드러운 리다이렉트 (URL 파라미터 없이)
			if (typeof window !== 'undefined') {
				// 현재 경로에서 locale 추출
				const currentPath = window.location.pathname;
				const pathSegments = currentPath.split('/').filter(Boolean);

				// 첫 번째 세그먼트가 locale인지 확인 (ko, en, etc.)
				const possibleLocale = pathSegments[0];
				const isValidLocale = /^[a-z]{2}(-[A-Z]{2})?$/.test(possibleLocale);
				const locale = isValidLocale ? possibleLocale : 'ko'; // 기본값 ko

				// 공개 페이지 패턴 (locale 포함)
				const publicPaths = [`/${locale}`, `/${locale}/login`, `/${locale}/signup`];
				const isPublicPath = publicPaths.some((path) => currentPath.startsWith(path)) || currentPath === '/';

				if (!isPublicPath) {
					// 인증이 필요한 페이지에서는 해당 locale의 메인 페이지로 깔끔하게 리다이렉트
					window.location.href = `/${locale}`;
				}
			}
		} catch (error) {
			console.error('[TokenManager] Error during token expiration handling:', error);
			// 최후의 수단으로도 깔끔한 리다이렉트
			if (typeof window !== 'undefined') {
				const currentPath = window.location.pathname;
				const pathSegments = currentPath.split('/').filter(Boolean);
				const possibleLocale = pathSegments[0];
				const isValidLocale = /^[a-z]{2}(-[A-Z]{2})?$/.test(possibleLocale);
				const locale = isValidLocale ? possibleLocale : 'ko';
				window.location.href = `/${locale}`;
			}
		}
	}

	// 강제 토큰 갱신 (기존 forceRefresh 호환)
	async forceRefresh(): Promise<boolean> {
		try {
			const response = await this.refreshToken();
			return !!response && response.ok;
		} catch (error) {
			console.error('[TokenManager] Force refresh failed:', error);
			return false;
		}
	}
}

// 싱글톤 인스턴스
let tokenManagerInstance: TokenManager | null = null;

export const getTokenManager = (): TokenManager => {
	if (typeof window === 'undefined') {
		// SSR 환경에서는 더미 객체 반환
		return {
			refreshToken: async () => null,
			forceRefresh: async () => false,
		} as unknown as TokenManager;
	}

	if (!tokenManagerInstance) {
		tokenManagerInstance = new TokenManager();
	}
	return tokenManagerInstance;
};

// 기존 코드 호환성을 위한 함수들
export const refresh = async (token: TokenData): Promise<Response> => {
	const manager = getTokenManager();
	const response = await manager.refreshToken();

	if (!response) {
		// 실패시 401 Response 반환
		return new Response(JSON.stringify({ message: 'REFRESHTOKEN_EXPIRED' }), {
			status: 401,
			headers: { 'Content-Type': 'application/json' },
		});
	}

	return response;
};

export const forceRefresh = async (): Promise<boolean> => {
	const manager = getTokenManager();
	return manager.forceRefresh();
};
